import { Box, Text, Flex, Button } from '@chakra-ui/react';
import { ExpandableClampText } from './ExpandableClampText';
import {
  Calendar,
  User,
  Pencil,
  Trash2,
  Play,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';

type TaskStatus = 'pending' | 'in_progress' | 'completed';

export interface TaskCardData {
  id: string;
  title: string;
  description: string | null;
  deadline: string;
  status: TaskStatus;
}

interface TaskListCardProps {
  task: TaskCardData;
  isOrganization: boolean;
  overdue: boolean;
  assignmentDisplay: string;
  onEdit: () => void;
  onDelete: () => void;
  onStart: () => void;
  onComplete: () => void;
}

const STATUS_STYLES: Record<
  TaskStatus,
  { label: string; fg: string; bg: string; accent: string }
> = {
  pending: { label: 'Pending', fg: '#B45309', bg: '#FEF3C7', accent: '#F59E0B' },
  in_progress: { label: 'In progress', fg: '#1D4ED8', bg: '#DBEAFE', accent: '#3B82F6' },
  completed: { label: 'Completed', fg: '#047857', bg: '#D1FAE5', accent: '#10B981' },
};

function formatDeadline(iso: string): { absolute: string; relative: string | null } {
  if (!iso) return { absolute: '—', relative: null };
  const d = new Date(iso);
  const now = new Date();
  const sameYear = d.getFullYear() === now.getFullYear();
  const absolute = d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: sameYear ? undefined : 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const diffMs = d.getTime() - now.getTime();
  if (diffMs < 0) return { absolute, relative: null };

  const diffMin = Math.round(diffMs / 60000);
  const diffHr = Math.round(diffMin / 60);
  const diffDay = Math.round(diffHr / 24);

  let relative: string | null = null;
  if (diffMin <= 1) relative = 'Soon';
  else if (diffMin < 60) relative = `in ${diffMin} min`;
  else if (diffHr < 24) relative = `in ${diffHr} hr`;
  else if (diffDay === 1) relative = 'Tomorrow';
  else if (diffDay < 7) relative = `in ${diffDay} days`;

  return { absolute, relative };
}

export function TaskListCard({
  task,
  isOrganization,
  overdue,
  assignmentDisplay,
  onEdit,
  onDelete,
  onStart,
  onComplete,
}: TaskListCardProps) {
  const status = STATUS_STYLES[task.status];
  const accentColor = overdue ? '#EF4444' : status.accent;
  const { absolute, relative } = formatDeadline(task.deadline);

  let actions: React.ReactNode = null;
  if (isOrganization) {
    actions = (
      <Flex gap="2" align="stretch">
        <Button
          flex="1"
          minH="40px"
          size="sm"
          borderRadius="lg"
          bg="volyx.action"
          color="white"
          fontWeight="semibold"
          _hover={{ bg: '#0063CC' }}
          onClick={onEdit}
        >
          <Flex as="span" align="center" gap="1.5" justify="center" w="full">
            <Pencil size={14} />
            Edit
          </Flex>
        </Button>
        <Button
          flex="1"
          minH="40px"
          size="sm"
          borderRadius="lg"
          bg="transparent"
          color="red.600"
          border="1px solid"
          borderColor="red.200"
          fontWeight="semibold"
          _hover={{ bg: 'red.50', borderColor: 'red.400' }}
          onClick={onDelete}
        >
          <Flex as="span" align="center" gap="1.5" justify="center" w="full">
            <Trash2 size={14} />
            Delete
          </Flex>
        </Button>
      </Flex>
    );
  } else if (task.status === 'pending') {
    actions = (
      <Button
        w="full"
        size="sm"
        borderRadius="lg"
        bg="#3B82F6"
        color="white"
        _hover={{ bg: '#2563EB' }}
        onClick={onStart}
      >
        <Flex as="span" align="center" gap="1.5" justify="center">
          <Play size={14} fill="currentColor" />
          Start task
        </Flex>
      </Button>
    );
  } else if (task.status === 'in_progress') {
    actions = (
      <Button
        w="full"
        size="sm"
        borderRadius="lg"
        bg="#10B981"
        color="white"
        _hover={{ bg: '#059669' }}
        onClick={onComplete}
      >
        <Flex as="span" align="center" gap="1.5" justify="center">
          <CheckCircle2 size={14} />
          Mark complete
        </Flex>
      </Button>
    );
  }

  return (
    <Box
      bg="white"
      borderRadius="xl"
      borderWidth="1px"
      borderColor="volyx.borderLight"
      borderLeftWidth="4px"
      borderLeftColor={accentColor}
      boxShadow="sm"
      transition="border-color 0.15s, box-shadow 0.15s"
      _hover={{ boxShadow: 'md' }}
    >
      <Box p="4" pl="3.5">
        <Flex align="flex-start" justify="space-between" gap="2" mb="1.5">
          <Box flex="1" minW="0">
            <ExpandableClampText
              text={task.title}
              lines={2}
              fontSize="sm"
              fontWeight="semibold"
              color="volyx.primary"
              mb="0"
            />
          </Box>
          <Box
            px="2.5"
            py="0.5"
            borderRadius="full"
            bg={status.bg}
            flexShrink={0}
          >
            <Text
              fontSize="10px"
              fontWeight="bold"
              color={status.fg}
              m="0"
              letterSpacing="0.04em"
              textTransform="uppercase"
            >
              {status.label}
            </Text>
          </Box>
        </Flex>

        {task.description ? (
          <ExpandableClampText text={task.description} lines={2} mb="3" />
        ) : null}

        <Flex direction="column" gap="1.5" mb={actions ? '3' : '0'}>
          <Flex align="center" gap="2" wrap="wrap" rowGap="1">
            <Flex align="center" gap="2">
              <Calendar size={14} color={overdue ? '#EF4444' : '#64748B'} />
              <Text
                fontSize="xs"
                color={overdue ? 'red.500' : 'volyx.textSecondary'}
                fontWeight={overdue ? 'semibold' : 'normal'}
                m="0"
              >
                {absolute}
              </Text>
            </Flex>
            {overdue ? (
              <Flex
                align="center"
                gap="1"
                px="1.5"
                py="0.5"
                borderRadius="md"
                bg="red.50"
              >
                <AlertCircle size={11} color="#DC2626" />
                <Text
                  fontSize="10px"
                  fontWeight="bold"
                  color="red.600"
                  m="0"
                  letterSpacing="0.04em"
                >
                  OVERDUE
                </Text>
              </Flex>
            ) : relative ? (
              <Text fontSize="11px" color="volyx.textSecondary" m="0">
                · {relative}
              </Text>
            ) : null}
          </Flex>
          <Flex align="center" gap="2">
            <User size={14} color="#64748B" />
            <Text fontSize="xs" color="volyx.textSecondary" m="0" lineClamp={1}>
              {assignmentDisplay}
            </Text>
          </Flex>
        </Flex>

        {actions}
      </Box>
    </Box>
  );
}
